/**
 * Tab Configuration
 *
 * Defines the tab structure for the Agent Configuration page.
 * Some tabs are conditionally shown based on feature toggles.
 *
 * IMPORTANT: Tab visibility has TWO modes:
 * 1. Expandable tabs (memory, classifier, team):
 *    - Visibility controlled by expandedTabs UI state
 *    - User expands to configure, actual enablement is inside the tab
 *
 * 2. Config-based tabs (schemas):
 *    - Visibility based on actual config values
 */

import { getConfigValue } from './fieldConfig';
import type { AgentConfiguration } from '@/types';

export type TabId =
  | 'basic'
  | 'hooks'
  | 'memory'
  | 'knowledge'
  | 'astrology'
  | 'emotion'
  | 'classifier'
  | 'team'
  | 'tools'
  | 'prompts'
  | 'advanced'
  | 'schemas'
  | 'versions';

/** Tabs that are controlled by expandedTabs UI state (not config) */
export const EXPANDABLE_TABS: TabId[] = ['memory', 'knowledge', 'astrology', 'emotion', 'classifier', 'team'];

export interface TabConfig {
  id: TabId;
  labelKey: string; // i18n key
  icon: string; // Lucide icon name
  order: number;
  /** For non-expandable tabs: function to determine visibility based on config */
  isVisible?: (config: Partial<AgentConfiguration>) => boolean;
  /** If true, this tab's visibility is controlled by expandedTabs UI state */
  expandable?: boolean;
  /** Description shown in tooltip */
  description?: string;
}

/**
 * Tab configuration
 */
export const TAB_CONFIG: TabConfig[] = [
  {
    id: 'hooks',
    labelKey: 'tabHooks',
    icon: 'Sparkles',
    order: 5,
    description: '功能开关和Hooks管道配置',
  },
  {
    id: 'basic',
    labelKey: 'tabBasic',
    icon: 'Settings',
    order: 10,
    description: '基本信息、模型配置和LLM参数',
  },
  {
    id: 'prompts',
    labelKey: 'tabPrompt',
    icon: 'FileText',
    order: 15,
    description: '系统提示词和模板配置',
  },
  {
    id: 'tools',
    labelKey: 'tabTools',
    icon: 'Wrench',
    order: 18,
    description: '工具配置',
  },
  {
    id: 'memory',
    labelKey: 'tabMemory',
    icon: 'Brain',
    order: 30,
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '记忆系统详细配置',
  },
  {
    id: 'knowledge',
    labelKey: 'tabKnowledge',
    icon: 'BookOpen',
    order: 35,
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '知识库配置',
  },
  {
    id: 'astrology',
    labelKey: 'tabAstrology',
    icon: 'Star',
    order: 38,
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '出生图、本命盘和行运配置',
  },
  {
    id: 'emotion',
    labelKey: 'tabEmotion',
    icon: 'Heart',
    order: 39,
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '情绪感知和意图分析配置',
  },
  {
    id: 'classifier',
    labelKey: 'tabClassifier',
    icon: 'Filter',
    order: 16, // Right after prompts (15), before tools (18)
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '响应分类器和技能配置',
  },
  {
    id: 'team',
    labelKey: 'tabTeam',
    icon: 'Users',
    order: 50,
    expandable: true, // Visibility controlled by expandedTabs, not config
    description: '团队模式配置',
  },
  {
    id: 'advanced',
    labelKey: 'tabAdvanced',
    icon: 'Cog',
    order: 80,
    description: '高级设置和消息处理',
  },
  {
    id: 'schemas',
    labelKey: 'tabSchemas',
    icon: 'FileJson',
    order: 90,
    isVisible: (config) => {
      const format = config.output_format;
      const schemas = config.output_schemas;
      return format === 'json' || (Array.isArray(schemas) && schemas.length > 0);
    },
    description: '输出Schema配置',
  },
  {
    id: 'versions',
    labelKey: 'tabVersions',
    icon: 'History',
    order: 100,
    description: '版本历史和JSON配置',
  },
];

/**
 * Get visible tabs based on current config and expanded tabs
 *
 * @param config - Agent configuration
 * @param expandedTabs - Set of tab IDs that are expanded (for expandable tabs)
 */
export function getVisibleTabs(
  config: Partial<AgentConfiguration>,
  expandedTabs: Set<TabId> = new Set()
): TabConfig[] {
  return TAB_CONFIG
    .filter(tab => {
      // For expandable tabs, check expandedTabs set
      if (tab.expandable) {
        return expandedTabs.has(tab.id);
      }
      // For config-based visibility tabs
      if (tab.isVisible) {
        return tab.isVisible(config);
      }
      // Always visible tabs
      return true;
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Check if a specific tab is visible
 */
export function isTabVisible(
  tabId: TabId,
  config: Partial<AgentConfiguration>,
  expandedTabs: Set<TabId> = new Set()
): boolean {
  const tab = TAB_CONFIG.find(t => t.id === tabId);
  if (!tab) return false;

  // For expandable tabs, check expandedTabs set
  if (tab.expandable) {
    return expandedTabs.has(tab.id);
  }
  // For config-based visibility
  if (tab.isVisible) {
    return tab.isVisible(config);
  }
  return true;
}

/**
 * Get tab config by ID
 */
export function getTabConfig(tabId: TabId): TabConfig | undefined {
  return TAB_CONFIG.find(t => t.id === tabId);
}

/**
 * Get the initial expanded tabs based on current config
 * This is used when first loading - expand tabs for features that are already enabled
 */
export function getInitialExpandedTabs(config: Partial<AgentConfiguration>): Set<TabId> {
  const expanded = new Set<TabId>();

  // Memory tab: expand if memory is enabled
  if (getConfigValue(config, 'memory.enabled')) {
    expanded.add('memory');
  }

  // Knowledge tab: expand if knowledge is enabled
  if (getConfigValue(config, 'knowledge_enabled')) {
    expanded.add('knowledge');
  }

  // Astrology tab: expand if astrology_plus is enabled
  if (getConfigValue(config, 'hooks.astrology_plus.enabled')) {
    expanded.add('astrology');
  }

  // Classifier tab: expand if classifier is enabled
  if (getConfigValue(config, 'hooks.classifier.enabled')) {
    expanded.add('classifier');
  }

  // Emotion tab: expand if emotion_enhance is enabled
  if (getConfigValue(config, 'hooks.emotion_enhance.enabled')) {
    expanded.add('emotion');
  }

  // Team tab: expand if team mode is enabled (agent_mode === 'team')
  if (config.agent_mode === 'team') {
    expanded.add('team');
  }

  return expanded;
}

export default TAB_CONFIG;
